import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { apiUrl, resolveTenantSlug, tokens } from '../lib/api';

/**
 * ═══ A PRINTABLE SHEET OF ID CARDS — STUDENTS OR STAFF ═══
 *
 * ⚠ THE PREVIEW IS THE REAL DOCUMENT, not an approximation of it. The iframe holds exactly the HTML
 * that will print, at exactly the millimetre positions, so what a school sees on screen is what
 * comes out of the printer. A preview built separately from the print output is a preview that
 * eventually lies.
 *
 * ⚠ AND IT IS FETCHED WITH THE TOKEN, then shown as a blob URL. Pointing the iframe straight at the
 * endpoint would have the browser issue that request on its own, without the Authorization header,
 * and the school would see a login page where their cards should be.
 *
 * ⚠ ONE COMPONENT FOR BOTH FACES, matching the server, where one renderer produces both sheets. Two
 * screens would drift the day either was adjusted, and the millimetres are the product.
 */

/** ⚠ `people` is optional. A class list carries a headcount and it is worth showing; a department
 *  list does not, and printing "· 0" beside every department reads as "nobody works here". */
export interface CardGroup { id: string; label: string; people?: number }

export interface CardSheetConfig {
  eyebrow: string;
  title: string;
  intro: ReactNode;
  /** Where the HTML comes from, without the query string. */
  path: string;
  /** Query parameter naming the chosen group, e.g. "academicClassId". */
  groupParam: string;
  groupLabel: string;
  /** Load the pickable groups. */
  loadGroups: () => Promise<CardGroup[]>;
  /** ⚠ When false, "everything" is a valid choice and the button works with nothing picked. */
  groupRequired: boolean;
  allLabel?: string;
  emptyHint: ReactNode;
}

export function CardSheet({ config }: { config: CardSheetConfig }) {
  const [groups, setGroups] = useState<CardGroup[] | null>(null);
  const [groupId, setGroupId] = useState('');
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [page, setPage] = useState<'A4' | 'Letter'>('A4');
  const [validUntil, setValidUntil] = useState('');
  const [cutMarks, setCutMarks] = useState(true);
  const [src, setSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const frame = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    config.loadGroups().then(setGroups).catch(() => setGroups([]));
  }, [config]);

  // ⚠ The previous blob URL is revoked whenever a new one replaces it. Each sheet carries every
  // photograph as base64 — tens of megabytes — and leaking one per preview would have a browser
  // holding a term's worth of them until the tab closes.
  const show = useCallback((url: string | null) => {
    setSrc((old) => { if (old) URL.revokeObjectURL(old); return url; });
  }, []);
  useEffect(() => () => { if (src) URL.revokeObjectURL(src); }, [src]);

  const build = async () => {
    if (config.groupRequired && !groupId) return;
    setBusy(true); setError(null);
    try {
      const q = new URLSearchParams({ page, orientation, cutMarks: String(cutMarks) });
      if (groupId) q.set(config.groupParam, groupId);
      if (validUntil.trim()) q.set('validUntil', validUntil.trim());

      const slug = resolveTenantSlug();
      const res = await fetch(apiUrl(`${config.path}?${q}`), {
        headers: {
          ...(slug ? { 'X-Tenant': slug } : {}),
          ...(tokens.access() ? { Authorization: `Bearer ${tokens.access()}` } : {}),
        },
      });

      if (!res.ok) {
        // ⚠ The server's own sentence, shown verbatim — "there is no active staff to print cards
        // for" tells the office what to do; "failed" sends them to support.
        const body = await res.json().catch(() => null);
        throw new Error(body?.message ?? `Could not build the cards (${res.status}).`);
      }

      show(URL.createObjectURL(await res.blob()));
    } catch (e) {
      show(null);
      setError(e instanceof Error ? e.message : 'Could not build the cards.');
    } finally { setBusy(false); }
  };

  // ⚠ Prints the IFRAME, not the page. window.print() here would print this screen — the controls,
  // the nav and a scaled-down thumbnail of the sheet.
  const print = () => frame.current?.contentWindow?.print();

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>{config.eyebrow}</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>{config.title}</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: '68ch' }}>
          {config.intro}
        </p>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 12, display: 'flex', gap: 14,
                                     alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 200 }}>
          <label className="label">{config.groupLabel}</label>
          <select className="input" value={groupId} disabled={groups === null}
            onChange={(e) => { setGroupId(e.target.value); show(null); }} style={{ width: '100%' }}>
            <option value="">
              {groups === null ? 'Loading…'
                : config.groupRequired ? `Choose a ${config.groupLabel.toLowerCase()}`
                : (config.allLabel ?? 'Everyone')}
            </option>
            {(groups ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.people ? `${c.label} · ${c.people}` : c.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">Card</label>
          <select className="input" value={orientation}
            onChange={(e) => { setOrientation(e.target.value as 'portrait' | 'landscape'); show(null); }}>
            <option value="portrait">Portrait — 9 a sheet</option>
            <option value="landscape">Landscape — 10 a sheet</option>
          </select>
        </div>

        <div>
          <label className="label">Paper</label>
          <select className="input" value={page}
            onChange={(e) => { setPage(e.target.value as 'A4' | 'Letter'); show(null); }}>
            <option value="A4">A4</option>
            <option value="Letter">Letter</option>
          </select>
        </div>

        <div>
          <label className="label">Valid until</label>
          <input className="input" value={validUntil} placeholder="31-03-2027" style={{ width: 130 }}
            onChange={(e) => setValidUntil(e.target.value)} />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, paddingBottom: 9 }}>
          <input type="checkbox" checked={cutMarks} onChange={(e) => setCutMarks(e.target.checked)} />
          Cut marks
        </label>

        <button className="btn btn-primary"
          disabled={(config.groupRequired && !groupId) || busy} onClick={build}>
          {busy ? 'Building…' : 'Build the sheet'}
        </button>

        {src && <button className="btn" onClick={print}>Print</button>}
      </div>

      {error && (
        <div className="card" style={{ padding: 14, marginBottom: 12, color: 'var(--danger)', fontSize: 13.5 }}>
          {error}
        </div>
      )}

      {!src && !busy && !error && (
        <div className="card" style={{ padding: 22, color: 'var(--ink-3)' }}>{config.emptyHint}</div>
      )}

      {src && (
        <>
          <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: '0 0 8px' }}>
            {/* ⚠ Said out loud, because "Fit to page" is on by default in some print dialogs and it
                rescales the sheet — after which nothing lines up with a guillotine. */}
            When printing, set <strong>Scale: 100%</strong> (not "Fit to page") and margins to
            <strong> None</strong>, or the cards will not be the right size.
          </p>
          <iframe ref={frame} src={src} title="ID cards"
            style={{ width: '100%', height: '78vh', border: '1px solid var(--line)', borderRadius: 10,
                     background: '#fff' }} />
        </>
      )}
    </div>
  );
}
